import { EFFECT_TYPES } from '../constants/effects';

let sharedAudioContext = null;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  sharedAudioContext ||= new AudioContextClass();
  return sharedAudioContext;
}

function playTone(context, { frequency, endFrequency = frequency, duration, type = 'sine', volume = 0.2, delay = 0 }) {
  const startTime = context.currentTime + delay;
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(endFrequency, 1), startTime + duration);
  gain.gain.setValueAtTime(volume, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration);
}

function playNoiseBurst(context, { duration, volume = 0.3, delay = 0 }) {
  const sampleCount = Math.floor(context.sampleRate * duration);
  const buffer = context.createBuffer(1, sampleCount, context.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let index = 0; index < sampleCount; index++) {
    samples[index] = (Math.random() * 2 - 1) * (1 - index / sampleCount);
  }
  const source = context.createBufferSource();
  const gain = context.createGain();
  source.buffer = buffer;
  gain.gain.value = volume;
  source.connect(gain).connect(context.destination);
  source.start(context.currentTime + delay);
}

const SYNTH_RECIPES = {
  [EFFECT_TYPES.EXPLOSION]: (context) => {
    playNoiseBurst(context, { duration: 0.6, volume: 0.4 });
    playTone(context, { frequency: 160, endFrequency: 40, duration: 0.5, type: 'square', volume: 0.25 });
  },
  [EFFECT_TYPES.FIZZ]: (context) => {
    for (let bubble = 0; bubble < 8; bubble++) {
      playTone(context, { frequency: 500 + Math.random() * 600, endFrequency: 1400, duration: 0.08, delay: bubble * 0.09, volume: 0.08 });
    }
  },
  [EFFECT_TYPES.FLASH]: (context) => {
    playTone(context, { frequency: 1200, endFrequency: 300, duration: 0.4, type: 'sawtooth', volume: 0.12 });
  },
  [EFFECT_TYPES.GLOW]: (context) => {
    playTone(context, { frequency: 440, endFrequency: 880, duration: 0.7, type: 'triangle', volume: 0.15 });
  },
  [EFFECT_TYPES.SLOW_CHANGE]: (context) => {
    playTone(context, { frequency: 330, endFrequency: 220, duration: 0.9, type: 'triangle', volume: 0.12 });
  },
  [EFFECT_TYPES.MELT]: (context) => {
    playTone(context, { frequency: 180, endFrequency: 260, duration: 0.8, type: 'sine', volume: 0.14 });
    playTone(context, { frequency: 270, endFrequency: 390, duration: 0.8, type: 'sine', volume: 0.08, delay: 0.1 });
  },
  [EFFECT_TYPES.NONE]: (context) => {
    playTone(context, { frequency: 300, endFrequency: 200, duration: 0.25, volume: 0.12 });
  },
  select: (context) => {
    playTone(context, { frequency: 660, endFrequency: 990, duration: 0.1, type: 'triangle', volume: 0.1 });
  },
};

/**
 * Plays a synthesized stand-in sound when a real audio file is missing.
 */
export function playSynthesizedSound(recipeKey) {
  const context = getAudioContext();
  const recipe = SYNTH_RECIPES[recipeKey];
  if (!context || !recipe) return;
  if (context.state === 'suspended') context.resume();
  recipe(context);
}
